export type SeoLanguage = 'ru' | 'en';

type SeoPayload = {
  language: SeoLanguage;
  projectName?: string;
  isDemoMode?: boolean;
};

type SeoText = {
  title: string;
  description: string;
  siteName: string;
};

const SITE_URL = 'https://fabrica.uno';
const DEFAULT_OG_IMAGE = `${SITE_URL}/training/intro.webp`;

const SEO_COPY: Record<SeoLanguage, SeoText> = {
  ru: {
    title: 'Фабрика Уно — 3D-мокапы для согласования дизайна',
    description:
      'Фабрика Уно помогает брендам согласовывать дизайн по 3D-мокапам и быстрее выводить продукты на рынок.',
    siteName: 'Фабрика Уно'
  },
  en: {
    title: 'Фабрика Уно — 3D mockups for design approval',
    description:
      'Фабрика Уно helps teams approve designs with 3D mockups and move products to market faster.',
    siteName: 'Фабрика Уно'
  }
};

const ROUTE_SEO: Record<SeoLanguage, Record<string, Pick<SeoText, 'title' | 'description'>>> = {
  ru: {
    '/product/': {
      title: 'Фабрика Уно — 3D-мокапы для согласования дизайна',
      description: 'Фабрика Уно — 3D-мокапы для согласования дизайна.'
    },
    '/b2b/': {
      title: 'Фабрика Уно — Закрывайте сделки быстрее конкурентов',
      description: 'Фабрика Уно — Закрывайте сделки быстрее конкурентов.'
    }
  },
  en: {
    '/product/': {
      title: 'Фабрика Уно — 3D mockups for design approval',
      description: 'Фабрика Уно — 3D mockups for design approval.'
    },
    '/b2b/': {
      title: 'Фабрика Уно — Close deals faster than competitors',
      description: 'Фабрика Уно — Close deals faster than competitors.'
    }
  }
};

function normalizePath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname.endsWith('/') ? pathname : `${pathname}/`;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, hreflang?: string): void {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    if (hreflang) el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(id: string, data: unknown): void {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(data);
}

export function updateSeo(payload: SeoPayload): void {
  const copy = SEO_COPY[payload.language];
  const path = normalizePath(window.location.pathname);
  const routeSeo = ROUTE_SEO[payload.language][path];
  const effectiveTitle = routeSeo?.title || copy.title;
  const effectiveDescription = routeSeo?.description || copy.description;
  const canonical = `${SITE_URL}${path === '/' ? '/' : path}`;

  const pageTitle = payload.isDemoMode
    ? payload.language === 'ru'
      ? 'Фабрика Уно — Демо режим'
      : 'Фабрика Уно — Demo mode'
    : payload.projectName
      ? `${payload.projectName} - ${copy.siteName}`
      : effectiveTitle;

  document.title = pageTitle;
  document.documentElement.lang = payload.language;

  upsertMeta('name', 'description', effectiveDescription);
  upsertMeta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
  upsertMeta('name', 'googlebot', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
  upsertMeta('name', 'bingbot', 'index, follow');
  upsertMeta('name', 'theme-color', '#0a0a0a');

  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:locale', payload.language === 'ru' ? 'ru_RU' : 'en_US');
  upsertMeta('property', 'og:site_name', copy.siteName);
  upsertMeta('property', 'og:title', pageTitle);
  upsertMeta('property', 'og:description', effectiveDescription);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:image', DEFAULT_OG_IMAGE);
  upsertMeta('property', 'og:image:alt', copy.title);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', pageTitle);
  upsertMeta('name', 'twitter:description', effectiveDescription);
  upsertMeta('name', 'twitter:image', DEFAULT_OG_IMAGE);

  upsertLink('canonical', canonical);
  upsertLink('alternate', canonical, 'ru');
  upsertLink('alternate', canonical, 'en');
  upsertLink('alternate', canonical, 'x-default');

  upsertJsonLd('seo-organization-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: copy.siteName,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`
  });

  upsertJsonLd('seo-webapp-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: copy.siteName,
    url: SITE_URL,
    inLanguage: payload.language,
    applicationCategory: 'DesignApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    },
    description: effectiveDescription
  });
}
